import { addDays, fromIsoDay, isoDay } from 'helper/timeLabels';
import { addStats, drawnDay, drawnReviews, emptyStats } from './crmStats';
import { read, write } from './crmStore';

/**
 * The api of the statistics page: the figures of the commercials day by day,
 * their weekly reviews and their goals. They answer from the demo database
 * until the api has them:
 *
 *   getStatsDays     POST admin/crm/stats/days        { from, to, people: [ids] }
 *   getStatsReviews  POST admin/crm/stats/reviews     { people: [ids] }
 *   saveStatsReview  POST admin/crm/stats/review      { weekStart, confidence, questioning, objections, energy, victory, challenge }
 *   getStatsGoals    POST admin/crm/stats/goals       { }
 *   saveStatsGoals   POST admin/crm/stats/goals/save  { people: { [personId]: { [goal]: value } } }
 *
 * getStatsDays answers a row per day from `from` to `to` (excluded; days
 * written « 2026-10-23 », in Martinique time), the figures of `people` added
 * up, 0 for a day to come:
 *   calls             the calls made (Wildix)
 *   pickedUp          those someone answered (every result but « Sans réponse »)
 *   discussions       those qualified « Répondu »
 *   rdvTaken          the appointments booked while prospecting, on the day booked
 *   rdvDone, rdvNoShow  those appointments held or missed, on their day
 *   inbound           the companies of the commercial created by a sign-up on the landing page
 *   sales             the companies of the commercial turned client
 *   revenue           for each of those sales, its invoices (Pennylane); before
 *                     its first invoice, an estimate from its timesheets (hours
 *                     × hourly rate × billing coefficient)
 *   revenueEstimated  the estimated part of `revenue`
 * A commercial (510) reads their own figures, the agency manager (500) those
 * of any commercial.
 *
 * getStatsReviews answers the weekly reviews of `people`, oldest first:
 *   [{ id, personId, weekStart, confidence, questioning, objections, energy,
 *      victory, challenge, createdAt }]  (each scale from 1 to 5)
 * A commercial reads their own, the agency manager those of the commercials
 * (decision of 23/10/2026). saveStatsReview writes the signed-in commercial's
 * review of a week, in the place of a former one, and answers it. When it has
 * a challenge, the api also creates the task « Défi de la semaine : … » of the
 * next Monday at 9:00, or renames the one a former review created; the demo
 * creates none.
 *
 * getStatsGoals answers { people: { [personId]: { [goal]: value } }, defaults }:
 * what each commercial aims at, set by the agency manager in the admin console
 * (saveStatsGoals, the trophies of the « Suivi-Progression » sheet), the
 * default for a goal not set. The goals (components/stats/statsGoals.js):
 * calls and rdvTaken a week, rdvDone, sales and revenue (€) a month.
 */

const DEFAULT_GOALS = {
  calls: 100,
  rdvTaken: 4,
  rdvDone: 12,
  sales: 2,
  revenue: 15000,
};
const SCALES = ['confidence', 'questioning', 'objections', 'energy'];

// The demo state `vide` has no history.
const isEmpty = (payload) => payload?.scenario === 'vide';
const peopleOf = (payload, me) =>
  payload?.people?.length ? payload.people : [me.id];

// The signed-in user's day so far is the one of the home page's call list.
const todayOf = (db, row) => {
  const made = db.calls?.made ?? [];
  return {
    ...row,
    calls: made.length,
    pickedUp: made.filter((call) => call.result !== 'no_answer').length,
    discussions: made.filter((call) => call.result === 'answered').length,
    rdvTaken: db.calls?.appointmentsBooked ?? 0,
  };
};

const daysBetween = (from, to) => {
  const days = [];
  for (let day = fromIsoDay(from); isoDay(day) < to; day = addDays(day, 1))
    days.push(isoDay(day));
  return days;
};

const reviewsOf = (db, personId, now) => {
  db.statsReviews = db.statsReviews ?? {};
  if (!db.statsReviews[personId])
    db.statsReviews[personId] = drawnReviews(personId, now);
  return db.statsReviews[personId];
};

const isScale = (value) => Number.isInteger(value) && value >= 1 && value <= 5;

const crmStatsApi = {
  getStatsDays: (payload, resolve, reject) =>
    read(payload, resolve, reject, (db, me) => {
      const now = new Date();
      const today = isoDay(now);
      return daysBetween(payload.from, payload.to).map((day) =>
        isEmpty(payload)
          ? emptyStats(day)
          : peopleOf(payload, me).reduce((total, personId) => {
              const row = drawnDay(personId, day, now);
              return addStats(
                total,
                personId === me.id && day === today ? todayOf(db, row) : row,
              );
            }, emptyStats(day)),
      );
    }),

  getStatsReviews: (payload, resolve, reject) =>
    read(payload, resolve, reject, (db, me) => {
      if (isEmpty(payload)) return [];
      const now = new Date();
      return peopleOf(payload, me)
        .flatMap((personId) => reviewsOf(db, personId, now))
        .sort((a, b) => a.weekStart.localeCompare(b.weekStart));
    }),

  saveStatsReview: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db, me) => {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(payload.weekStart ?? ''))
        throw new Error('La semaine du bilan manque.');
      if (!SCALES.every((scale) => isScale(payload[scale])))
        throw new Error('Chaque échelle se note de 1 à 5.');
      const review = {
        id: `${me.id}-${payload.weekStart}`,
        personId: me.id,
        weekStart: payload.weekStart,
        ...Object.fromEntries(SCALES.map((scale) => [scale, payload[scale]])),
        victory: (payload.victory ?? '').trim(),
        challenge: (payload.challenge ?? '').trim(),
        createdAt: new Date().toISOString(),
      };
      const others = isEmpty(payload)
        ? []
        : reviewsOf(db, me.id, new Date()).filter(
            (other) => other.weekStart !== review.weekStart,
          );
      db.statsReviews = {
        ...db.statsReviews,
        [me.id]: [...others, review].sort((a, b) =>
          a.weekStart.localeCompare(b.weekStart),
        ),
      };
      return review;
    }),

  getStatsGoals: (payload, resolve, reject) =>
    read(payload, resolve, reject, (db) => ({
      people: { ...db.statsGoals },
      defaults: DEFAULT_GOALS,
    })),

  saveStatsGoals: (payload, resolve, reject) =>
    write(payload, resolve, reject, (db) => {
      const people = payload.people ?? {};
      const valid = Object.values(people).every((goals) =>
        Object.entries(goals).every(
          ([key, value]) =>
            key in DEFAULT_GOALS && Number.isInteger(value) && value >= 0,
        ),
      );
      if (!valid) throw new Error('Un objectif est un nombre entier positif.');
      db.statsGoals = { ...db.statsGoals };
      Object.entries(people).forEach(([personId, goals]) => {
        db.statsGoals[personId] = { ...db.statsGoals[personId], ...goals };
      });
      return { people: { ...db.statsGoals }, defaults: DEFAULT_GOALS };
    }),
};

export default crmStatsApi;
